"use client";
import {
  Image,
  Text,
  Flex,
  Skeleton,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  SkeletonText,
  useDisclosure,
  Box,
  Button,
} from "@chakra-ui/react";
import { MinusIcon, AddIcon } from "@chakra-ui/icons";
import Head from 'next/head';
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useAppContext } from "hooks";
import { useGetModel } from "queries";
import { useFalForLoraTxtToImg, useFalForLoraImgToImg } from "queries"; // Adjust path
import { LuInstagram, LuTwitter } from "react-icons/lu";
import Link from "next/link";
import { Jumbotron, FluxPlayground, ModelGenerationsDisplay, FluxPlaygroundRemix,FluxPlaygroundNormal} from "components";
import { SocialMediaAccount, Lora } from "types";

const ModelPageFlux = ({ name }: { name: string }) => {
  const pathname = usePathname();
  const [selectedRatio, setSelectedRatio] = useState<string>("portrait_16_9"); // Add this state

  const router = useRouter();
  const { jwt, profile } = useAppContext();
  const { data: model, isLoading } = useGetModel(pathname.includes("mymodels"), name);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [prompt, setPrompt] = useState("");
   // Separate state variables for prompts
   const [normalPrompt, setNormalPrompt] = useState<string>(""); // For FluxPlaygroundNormal
   const [playgroundPrompt, setPlaygroundPrompt] = useState<string>("");// For FluxPlayground
   const [remixPrompt, setRemixPrompt] = useState<string>(""); // For FluxPlaygroundRemix
   const [constantLora, setCurrentConstantLora] = useState<Lora>({
    path: `https://s3.eu-west-1.amazonaws.com/safetensors.flux/PublicModels/default_model`,
    scale: 1.0,
  });
  const [isRemixMode, setIsRemixMode] = useState(false); 
  const [isNormalMode, setIsNormalMode] = useState(true); 
  const [refetchGenerations, setRefetchGenerations] = useState<(() => void) | null>(null);


  const [dynamicLoras, setDynamicLoras] = useState<Lora[]>([]);
 
  const { mutate: generateTxtToImg, data: generatedData, status: txtToImgStatus, error: txtToImgError } = useFalForLoraTxtToImg();

  const { loading: imgToImgLoading, error: imgToImgError, generateImage: generateImgToImg } = useFalForLoraImgToImg();

  const isGenerating = isRemixMode ? imgToImgLoading : txtToImgStatus === "pending";
  const error = isRemixMode ? imgToImgError : txtToImgError;

 
  useEffect(() => {
    if (model?.username) {
      setCurrentConstantLora({
        path: model.user_id 
          ? `https://s3.eu-west-1.amazonaws.com/safetensors.flux/UserModels/${model.username}_${model.user_id}.safetensor`
          : `https://s3.eu-west-1.amazonaws.com/safetensors.flux/PublicModels/${model.username}`,
        scale: 1.0,
      });
    }
  }, [model]);


   useEffect(() => {
    if (!isLoading && (!model || !model.isFlux)) {
      console.log("Redirecting because model is not Flux enabled.");
      router.replace("/");
    }
  }, [isLoading, model, router]);

  const handleConstantLoraChange = (newScale: number) => {
    const updatedLora = { ...constantLora, scale: newScale };
    setCurrentConstantLora(updatedLora);
  };
  const handleGenerateImage = (dynamicLoras: Lora[], imageSize: string, numGenerations: number) => {
    // Combine constantLora and dynamicLoras into one array
    const allLoras = dynamicLoras.some(
      (lora) => lora.path === constantLora.path
    )
      ? dynamicLoras
      : [constantLora, ...dynamicLoras];
  
    console.log("Sending LoRAs to mutation:", allLoras);
  
    // Determine which prompt to use
    const currentPrompt = isNormalMode ? normalPrompt : playgroundPrompt;
  
    // Pass all required parameters to the mutation
    generateTxtToImg({ prompt: currentPrompt, loras: allLoras, image_size: imageSize,  num_images: numGenerations });
  };
  
  
  
  

  const handleGenerateRemixImage = (imageFile: File) => {
    const constantLora: Lora = model?.user_id
      ? {
          path: `https://s3.eu-west-1.amazonaws.com/safetensors.flux/UserModels/${model?.username}_${model?.user_id}.safetensor`,
          scale: 1.0,
        }
      : {
          path: `https://s3.eu-west-1.amazonaws.com/safetensors.flux/PublicModels/${model?.username || "default_model"}`,
          scale: 1.0,
        };
  
    generateImgToImg(imageFile, remixPrompt || "", constantLora);
  };
  
  
  return (
    <Flex direction="column">
      <Head>
        <title>{model ? `${model.display_name} AI Images | DeepMode AI Character Reimagined` : 'Model Page'}</title>
        <meta name="description" content={model ? `Discover ${model.display_name}, DeepMode's advanced AI-generated character.` : 'Model Page'} />
      </Head>
      <Jumbotron background={{ base: "none", md: "linear-gradient(to bottom, #16181F, #121C3D)" }}>
        <Flex
          w="100%"
          maxW="1200px"
          mx="auto"
          align="center"
          justify={{ base: "center", md: "flex-start" }}
          py={{ base: "30px", md: "50px" }}
          px={{ base: "20px", md: "40px" }}
          direction={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          {/* Model Image */}
          {isLoading ? (
            <Skeleton h={{ base: "150px", md: "200px" }} w={{ base: "150px", md: "200px" }} borderRadius="12px" />
          ) : (
            <Image
              h={{ base: "150px", md: "200px" }}
              w={{ base: "150px", md: "200px" }}
              src={model?.picture || "default_image_url"}
              alt="image"
              objectFit="cover"
              borderRadius="12px"
              mb={{ base: "20px", md: "0" }}
            />
          )}

          {/* Model Info */}
          <Flex ml={{ base: "0", md: "50px" }} direction="column" align={{ base: "center", md: "flex-start" }}>
            {isLoading ? (
              <SkeletonText noOfLines={1} fontSize={{ base: "32px", md: "56px" }} w={{ base: "200px", md: "300px" }} />
            ) : (
              <Text
                fontSize={{ base: "32px", md: "56px" }}
                fontWeight="extrabold"
                bgGradient="linear(to-r, #FFE1FF 10%, #A4DBEE 50%)"
                bgClip="text"
              >
                {model?.display_name}
              </Text>
            )}

            {/* Social Media Icons */}
            <Flex justify="center" mt={{ base: "10px", md: "0" }} mb="10px">
              {model?.social_media_accounts?.map((social: SocialMediaAccount, index: number) => (
                <Box key={index} mx="10px">
                  <Link target="_blank" href={social.link}>
                    {social.social_media.name === "twitter" ? <LuTwitter size={20} /> : <LuInstagram size={20} />}
                  </Link>
                </Box>
              ))}
            </Flex>

            {/* Model Description */}
            {isLoading ? (
              <SkeletonText mt="4" noOfLines={3} spacing="4" w={{ base: "90%", md: "100%" }} />
            ) : (
              <Text maxW={{ base: "90%", md: "550px" }} color="#7E95AA" fontSize={{ base: "12px", md: "13px" }} mt="10px">
                {model?.description}
              </Text>
            )}

            {/* CTA Button */}
            {!pathname.includes("create") && (
              <Button
                mt="25px"
                p={6}
                borderRadius="16px"
                color="white"
                bg="#FF1B6D"
                onClick={() => (!jwt ? onOpen() : router.push(`${pathname}/create`))}
                fontSize={{ base: "12px", md: "14px" }}
              >
                Play with this model
              </Button>
            )}

            
          </Flex>
        </Flex>
      </Jumbotron>

                  {/* Flux Playground Section */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg={{ base: "none", md: "#050918" }}
        minH="100vh"
        pt={10} // Apply top padding only
        pb={0}  
        mt={{ base: "100px", md: "0" }}
        px={{
          base: "10px", 
          sm: "20px", 
          md: "30px", 
          lg: "60px", 
          xl: "60px", 
          "2xl": "20%",
        }}      >
        {/* Title */}
        <Text
          style={{
            background: "linear-gradient(to right, #FFE1FF 0%, #A4DBEE 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            MozBackgroundClip: "text",
          }}
          fontSize={{ base: "38px", md: "38px", lg: "56px" }}
          fontWeight="bold"
          whiteSpace="nowrap"
          mb={8}
          textAlign="center"
        >
          Flux Playground
        </Text>

        {/* Mode Label and Prompt Button */}
        <Flex
      mt="25px"
      borderRadius="20px"
      w="100%"
      bg="transparent"
      direction="column"
      px={{ base: "15px", md: "25px" }}
      py="18px"
      border="1px solid gray"
        >
          <Text color="white" fontWeight={600}>
            Mode
          </Text>
          <Flex  mt="10px" 
          flexWrap={{ base: "wrap", md: "nowrap" }}
          gap={{ base: 2, md: 0 }}>
          <Button
              onClick={() => {
                setIsNormalMode(true);
                setIsRemixMode(false);
                setPlaygroundPrompt("")
              }}
              style={{
                background:
                  isNormalMode
                    ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    : "transparent",
                color: isNormalMode ? "black" : "#F5F5F5", // Optional: Change text color when selected
                border: isNormalMode ? 0 : "2px solid gray",
              }}
              size={{ base: "sm", md: "md" }}
            minW={{ base: "auto", md: "initial" }}
              borderRadius="32px"
            >
              Normal
            </Button>
            <Button
              onClick={() => {
                setIsNormalMode(false);
                setIsRemixMode(false);
                setPlaygroundPrompt("")

              }}
              style={{
                background:
                  !isNormalMode && !isRemixMode
                    ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    : "transparent",
                color: !isNormalMode && !isRemixMode ? "black" : "#F5F5F5", // Optional: Change text color when selected
                border: !isNormalMode && !isRemixMode ? 0 : "2px solid gray",
              }}
              size={{ base: "sm", md: "md" }}
            minW={{ base: "auto", md: "initial" }}
              borderRadius="32px"
              ml={["0px", "15px"]}
              
            >
              Prompt
            </Button>
            <Button
              onClick={() => {
                setIsNormalMode(false);
                setIsRemixMode(true);
                setPlaygroundPrompt("")

              }}
              style={{
                background:
                  isRemixMode
                    ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    : "transparent",
                color: isRemixMode ? "black" : "#F5F5F5", // Optional: Change text color when selected
                border: isRemixMode ? 0 : "2px solid gray",
              }}
              size={{ base: "sm", md: "md" }}
            minW={{ base: "auto", md: "initial" }}
              borderRadius="32px"
              ml={["0px", "15px"]}
              >
              Remix
            </Button>
            <Flex ml={["0px", "15px"]} align="center" direction="column">
          <Button
              onClick={() => {
                window.open("https://magikface.com/?fpr=deepmode", "_blank");
              }}
              style={{
                background: "transparent",
                color: "#F5F5F5",
                border: "2px solid gray",
              }}
              borderRadius="32px"
              size={{ base: "sm", md: "md" }}
              minW={{ base: "auto", md: "initial" }}
            >
              Video
            </Button>
            <Text  onClick={() => {
                window.open("https://magikface.com/?fpr=deepmode", "_blank");
              }} 
              textDecoration="underline"
              fontWeight="700"
              fontSize={{ base: "8px", md: "10px" }}
              color="grey"
              cursor="pointer"
              >
              Magik Face
            </Text>
            </Flex>
          </Flex>

        </Flex>
      {/* Conditionally Render Playground */}
      {isRemixMode ? (
          <FluxPlaygroundRemix
          prompt={remixPrompt}
          setPrompt={setRemixPrompt}
            modelUsername={model?.username || "default_model"}
            isGenerating={isGenerating}
            error={error ? error.toString() : null}
            constantLora={constantLora} 
            refetchTrigger={() => {
              if (refetchGenerations) {
                refetchGenerations(); 
              }
            }}
            model={model}
          />
        ) : isNormalMode ? (
          <FluxPlaygroundNormal
            prompt={normalPrompt}
            setPrompt={setNormalPrompt}
            handleGenerateImage={handleGenerateImage} 
            dynamicLoras={dynamicLoras}
            setDynamicLoras={setDynamicLoras}
            generatedData={generatedData}
            isGenerating={isGenerating}
            error={typeof error === 'string' ? new Error(error) : error}
            mt="35px"
            refetchTrigger={() => {
              if (refetchGenerations) {
                refetchGenerations();
              }
            }}
            constantLora={constantLora}
            model={model}
          />
        ) : (
          <FluxPlayground
          prompt={playgroundPrompt}
          setPrompt={setPlaygroundPrompt}
          handleGenerateImage={handleGenerateImage} 
          handleConstantLoraChange={handleConstantLoraChange}
          generatedData={generatedData}
          isGenerating={isGenerating}
          model={model}
          error={typeof error === 'string' ? new Error(error) : error}
          mt="35px"
          refetchTrigger={() => {
            if (refetchGenerations) {
              refetchGenerations();
            }
          }}
          constantLora={constantLora} 
        />
        )}
      </Flex>
      <Box
        px={{ base: "20px", md: "40px" }}
        py="20px"
        pb="150px"
        bg={{ base: "transparent", md: "#050918" }} 
        mt={{ base: "0px", md: "-10px" }}
      >
          <Text mt="10px" fontWeight={700} textAlign="left" ml="20px">
            Missing a Tag?{" "}
            <span style={{ textDecoration: "underline", fontWeight: 600 }}>
              <a target="_blank" href="https://discord.gg/CMwtccKthy" rel="noopener noreferrer">
                Join our Discord to suggest it
              </a>
            </span>
          </Text>
      </Box>
      <CustomAccordion />
    </Flex>
  );
};

const CustomAccordion = () => {
  const faqData = [
      {
        question: "What is DeepMode and how does it work?",
        answer: (
          <Text>
            DeepMode is a Generative AI platform that helps creators make their AI clones. Creators can generate beautiful AI photos, whether realistic or non-realistic, with unlimited freedom. With DeepMode, creators can unleash their imagination and create unique photos with unlimited freedom.
          </Text>
        ),
      },
      {
        question: "How much does DeepMode cost?",
        answer: (
          <Text>
            DeepMode offers various top-up plans: Check{" "}
            <Link style={{ textDecoration: "underline" }} href="/payments">
              this page
            </Link>{" "}
            for more details.
          </Text>
        ),
      },
      {
        question: "Can I try DeepMode for free?",
        answer: (
          <Text>
            Yes, every new user automatically gets 20 credits. However, to unlock training an AI model you will need to top-up your balance.
          </Text>
        ),
      },
      {
        question: "Are my DeepMode creations private?",
        answer: (
          <Text>
            Yes, your creations on DeepMode are kept private and only you can view them. You also have the option to delete specific models or images you don&apos;t like.
          </Text>
        ),
      },
      {
        question: "Does DeepMode store my uploaded images?",
        answer: (
          <Text>
            No, your uploaded photos are deleted immediately from our servers after training. In addition, the Model itself is being deleted from our servers after 30 days. If you wish to extend the model storage, you can do so.
          </Text>
        ),
      },
      {
        question: "Can I use my creations anywhere?",
        answer: (
          <Text>
            Yes. Your generated images are yours to use on any other platform.
          </Text>
        ),
      },
      {
        question: "How is DeepMode different from Midjourney, Stable Diffusion, or Dall-E?",
        answer: (
          <Text>
            Unlike other AI platforms such as Midjourney, Stable Diffusion, or DALL-E, DeepMode stands out for its exclusive focus on the generation of AI clones. DeepMode is committed to providing a fully uncensored experience, allowing users unlimited creative freedom without any content restrictions.
          </Text>
        ),
      },
      {
        question: "Does DeepMode run on the Cloud?",
        answer: (
          <Text>
            Yes, DeepMode runs in the cloud. This means that you can use the platform and view your generations across various devices.
          </Text>
        ),
      },
      {
        question: "Does DeepMode offer an Affiliate program?",
        answer: (
          <Text>
            Absolutely! Become a partner and earn cash rewards every month. <a href="https://deepmode.firstpromoter.com/" style={{ textDecoration: "underline" }} target="_blank">Find out more about our Affiliate Program.</a>
          </Text>
        ),
      },
      {
        question: "Can I create videos with DeepMode?",
        answer: (
          <Text>
            {"Currently, video generation is not supported. However, we're working on this feature and plan to introduce it soon."}
          </Text>
        ),
      },
      {
        question: "Can I get a refund?",
        answer: (
          <Text>
            {"We offer refunds for purchases made within the last 7 days, provided no credits have been used. If you believe you're eligible for a refund, please "}
            <a style={{ textDecoration: "underline" }} href="/contact-us">
              contact us here.
            </a>
          </Text>
        ),
      },
      {
        question: "How do I get in touch with the DeepMode team?",
        answer: (
          <Text>
            We usually hang out on{" "}
            <a style={{ textDecoration: "underline" }} href="https://discord.gg/CMwtccKthy">
              Discord
            </a>
            . You can also reach out to us via{" "}
            <a style={{ textDecoration: "underline" }} href="mailto:info@deepmode.com">
              e-mail
            </a>
            .
          </Text>
        ),
      },
      {
        question: "Can I access DeepMode through an API?",
        answer: (
          <Text>
            {"Yes, you can access DeepMode through API and seamlessly integrate it into your application. "}
            <a style={{ textDecoration: "underline" }} href="/docs">
              Check our API integration documentation.
            </a>
          </Text>
        ),
      },
    ];

 
return (
  <>
  <Flex
      mt="20px"
      mb="20px"
      p={{ base: "20px", md: "100px" }} // Add more padding for desktop view
      direction="column"
  >
      <Text fontSize="40px" fontWeight="semibold">
          FAQs - Frequently Asked Questions
      </Text>
      <Accordion mt="10px" allowToggle>
          {faqData.map((item, index) => (
              <AccordionItem key={index}>
                  {({ isExpanded }) => (
                      <>
                          <AccordionButton>
                              <Box flex="1" textAlign="left">
                                  {item.question}
                              </Box>
                              {isExpanded ? <MinusIcon fontSize="12px" /> : <AddIcon fontSize="12px" />}
                          </AccordionButton>
                          <AccordionPanel pb={4}>{item.answer}</AccordionPanel>
                      </>
                  )}
              </AccordionItem>
          ))}
      </Accordion>
  </Flex>
</>
);
};

export default ModelPageFlux;
